import { useState, useEffect, useRef, useCallback } from 'react';
import { produce } from 'immer';
import { isFunction } from './Utils';
import { fetchEntityById, fetchPagedList, getPowerBIGroupList, IGridFilterVariables } from '../../services/ApiServices';

export function useStateWithEffects<T>(props: T, onValueChanged: (e: T) => void): any[] {
  // eslint-disable-next-line prefer-const
  let [value, setValue] = useState<T>(props);

  const setState = (newValue: T) => {
    value = newValue;
    setValue(value);
    onValueChanged(value);
  };

  return [value, setState];
}

export function useStickyState<T>(defaultValue: T, key: string): any[] {
  const [value, setValue] = useState<T>(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null ? (JSON.parse(stickyValue) as T) : defaultValue;
  });
  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function useStickyStateWithEffects<T>(defaultValue: T, key: string, onValueChanged: (e: T) => void): any[] {
  const initState = (): T => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
  };
  const [value, setValue] = useStateWithEffects<T>(initState(), onValueChanged);
  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

interface IUsePopupProps {
  initialIsMenuOpen?: boolean;
  ignoredElementsClasses?: string[];
  shouldReactOnEsc?: boolean;
  shouldReactOnClickOutside?: boolean;
}

export const usePopup = (usePopupProps: IUsePopupProps = {}): any[] => {
  const {
    initialIsMenuOpen = false,
    ignoredElementsClasses: rawElementsClasses = [],
    shouldReactOnEsc = true,
    shouldReactOnClickOutside = true,
  } = usePopupProps;
  const ignoredElementsClasses = rawElementsClasses.concat('.k-popup');

  const [isMenuOpen, setIsMenuOpen] = useState(initialIsMenuOpen);

  useEffect(() => {
    if (!shouldReactOnEsc) return;
    const closePopup = (e: any) => {
      if (e.code === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', closePopup);
    return () => document.removeEventListener('keydown', closePopup);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!shouldReactOnClickOutside) return;
    const closePopup = (e: any) => {
      const isClickOnPopup = ignoredElementsClasses.some((cssClass) => e.target.closest(cssClass));
      if (!isClickOnPopup && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('click', closePopup);
    return () => document.removeEventListener('click', closePopup);
  }, [isMenuOpen]);

  return [isMenuOpen, setIsMenuOpen];
};

export function useImmerState<T extends object>(initialState: T): [T, any] {
  const [state, setState] = useState(initialState);

  const setImmerState = useRef((fnOrObject: any) => {
    if (isFunction(fnOrObject)) {
      const fn = fnOrObject;
      setState((curState) => produce(curState, fn));
    } else {
      const newState = fnOrObject;
      setState((curState) => ({ ...curState, ...newState }));
    }
  });

  return [state, setImmerState.current];
}

export const useRestPowerBIGroupsQuery = (url : string | undefined, params: any) => {
  const [state, setState] = useState<{ loading: boolean; error: any; data: any }>({
    loading: !!url,
    error: null,
    data: null,
  });
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    getPowerBIGroupList<any>(url, params)
      .then((data) => {
        !cancelled && setState({ loading: false, error: null, data });
      })
      .catch((error) => {
        !cancelled && setState({ loading: false, error, data: null });
      });
      return () => { cancelled = true; };
  }, [url, JSON.stringify(params)]);
  return state;
}

export interface IRestConnection {
  edges: { node: any }[];
  totalCount: number;
}

/**
 * Fetches a PaginatedList from a REST list endpoint and exposes it with an Apollo-style
 * contract (loading/error/data/refetch), with data shaped as a GraphQL-style connection
 * ({edges: [{node}], totalCount}) for compatibility with existing grid rendering code.
 * Pass url=undefined to disable (skip).
 */
export const useRestPagedQuery = (url: string | undefined, variables: IGridFilterVariables) => {
  const [state, setState] = useState<{ loading: boolean; error: any; data: IRestConnection | null }>({
    loading: !!url,
    error: null,
    data: null,
  });
  const [fetchTick, setFetchTick] = useState(0);
  // Re-fetch on any change of the effective filter, compared by value: the variables object is
  // rebuilt on every render, so identity comparison would loop forever.
  const variablesKey = JSON.stringify(variables);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetchPagedList<any>(url, JSON.parse(variablesKey))
      .then((page) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          data: {
            // REST pocos serialize enum names in PascalCase ("Active") while the grid's status logic
            // expects the GraphQL enum spelling ("ACTIVE") - normalize at the boundary.
            edges: page.value.map((item: any) => ({
              node: { ...item, status: typeof item.status === 'string' ? item.status.toUpperCase() : item.status },
            })),
            totalCount: page.count,
          },
        });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [url, variablesKey, fetchTick]);

  const refetch = useCallback(() => setFetchTick((tick) => tick + 1), []);
  return { ...state, refetch };
};

/**
 * REST twin of a GetById query for detail pages: fetches a single entity by id from a REST
 * endpoint and exposes it with the Apollo-style contract (loading/error/data/refetch).
 * Pass url=undefined or id=undefined to disable (skip).
 */
export const useRestByIdQuery = (url: string | undefined, id: string | undefined) => {
  const [state, setState] = useState<{ loading: boolean; error: any; data: any }>({
    loading: !!url && !!id,
    error: null,
    data: null,
  });
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    if (!url || !id) return;
    let cancelled = false;
    // Only flip to loading when there's no data yet (first load). On a refetch of an
    // already-loaded record, keep showing the existing data instead - matches Apollo's
    // notifyOnNetworkStatusChange: false default (used by the GraphQL-mode ViewPanel query),
    // which keeps `loading` false during background refetches. Without this, ViewPanel's
    // `if (loading) return <spinner>` gate unmounts the whole form on every refetch (e.g. after
    // a status-change confirm), remounting FieldCascadeDataBox and re-firing its cascade fetch.
    setState((prev) => ({ ...prev, loading: !prev.data, error: null }));
    fetchEntityById<any>(url, id)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [url, id, fetchTick]);

  const refetch = useCallback(() => setFetchTick((tick) => tick + 1), []);
  return { ...state, refetch };
};
